<template>
  <div id="app" class="kui:h-full kui:overflow-hidden kui:bg-gray-01 kui:isolate">
    <RouteProgressBar />
    <router-view />
    <CommandPalette />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useThemeStore } from '@/stores/theme'
import { useAuthStore } from '@/stores/auth'
import apiClient from '@/services/apiClient'
import CommandPalette from '@/components/CommandPalette.vue'
import RouteProgressBar from '@/components/RouteProgressBar.vue'
import { useCommandPalette } from '@/composables/useCommandPalette'
import type { PaletteCommand } from '@/composables/useCommandPalette'
import type { Feature, Effort } from '@/types'

const router = useRouter()
const themeStore = useThemeStore()
const authStore = useAuthStore()
const palette = useCommandPalette()

// Register static global navigation commands
palette.registerCommands([
  {
    id: 'nav:home',
    label: 'Go to Home',
    category: 'navigation',
    icon: 'home',
    keywords: ['home', 'dashboard', 'teams', 'products'],
    execute: () => router.push({ name: 'dashboard-teams' })
  },
  {
    id: 'nav:settings',
    label: 'Go to Settings',
    category: 'navigation',
    icon: 'settings',
    keywords: ['preferences', 'integrations', 'account', 'config'],
    execute: () => router.push({ name: 'settings' })
  },
  {
    id: 'nav:clusters',
    label: 'Go to Clusters',
    category: 'navigation',
    icon: 'hub',
    keywords: ['clusters', 'signals', 'federation'],
    execute: () => router.push({ name: 'meta-signals', query: { view: 'clusters' } })
  },
  {
    id: 'action:toggle-theme',
    label: 'Toggle Dark/Light Theme',
    category: 'action',
    icon: 'dark_mode',
    keywords: ['theme', 'dark', 'light', 'mode', 'appearance'],
    execute: () => themeStore.toggle()
  },
  {
    id: 'action:toggle-palette',
    label: 'Toggle Kipu / Cybersyn Mode',
    category: 'action',
    icon: 'palette',
    keywords: ['theme', 'palette', 'kipu', 'cybersyn', 'cybernetic', 'mode', 'colors', 'appearance'],
    execute: () => themeStore.setPalette(
      themeStore.palettePreference === 'kipu' ? 'cybernetic' : 'kipu'
    )
  }
])

// Register dynamic team commands whenever the user's teams change
function registerTeamCommands() {
  const teams = authStore.user?.teams ?? []
  // Deregister any previously registered team commands
  palette.deregisterCommandsByPrefix('nav:team:')
  teams.forEach(team => {
    palette.registerCommands([{
      id: `nav:team:${team.id}`,
      label: `Go to ${team.name}`,
      category: 'navigation',
      icon: 'groups',
      keywords: ['team', team.name.toLowerCase()],
      execute: () => router.push({ name: 'team', params: { id: team.id } })
    }])
  })
}

watch(() => authStore.user?.teams, registerTeamCommands, { immediate: true })

// Register dynamic feature/effort content commands so they're searchable by name from anywhere
async function registerWorkItemCommands() {
  const teams = authStore.user?.teams ?? []

  palette.deregisterCommandsByPrefix('content:feature:')
  palette.deregisterCommandsByPrefix('content:effort:')

  if (teams.length === 0) return

  const perTeam = await Promise.all(teams.map(async team => {
    const [featuresRes, effortsRes] = await Promise.all([
      apiClient.get<Feature[]>('/api/features', { params: { teamId: team.id } }).catch(() => ({ data: [] as Feature[] })),
      apiClient.get<Effort[]>(`/api/teams/${team.id}/efforts`).catch(() => ({ data: [] as Effort[] }))
    ])
    return { features: featuresRes.data ?? [], efforts: effortsRes.data ?? [] }
  }))

  const featureCommands: PaletteCommand[] = perTeam.flatMap(t => t.features).map(feature => ({
    id: `content:feature:${feature.id}`,
    label: feature.name,
    category: 'content',
    icon: 'flag',
    keywords: [feature.slug, feature.effortTitle, 'feature'].filter((k): k is string => !!k),
    execute: () => router.push({ name: 'workspace', params: { id: feature.id } })
  }))

  const effortCommands: PaletteCommand[] = perTeam.flatMap(t => t.efforts).map(effort => ({
    id: `content:effort:${effort.id}`,
    label: effort.title,
    category: 'content',
    icon: 'folder_copy',
    keywords: ['initiative', 'effort'],
    execute: () => router.push({ name: 'effort', params: { teamId: effort.teamId, effortId: effort.id } })
  }))

  if (featureCommands.length > 0 || effortCommands.length > 0) {
    palette.registerCommands([...featureCommands, ...effortCommands])
  }
}

watch(() => authStore.user?.teams, registerWorkItemCommands, { immediate: true })

onMounted(() => palette.setupKeyboardShortcut())
onUnmounted(() => palette.teardownKeyboardShortcut())
</script>
